// ----------------------------------------------------------------------
// <copyright file="Izenda.ImportDataModel.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { isEmpty, forEach, find, isNil } from 'lodash/fp';
import constants from 'logic/data/Izenda.ImportDataModelConstants';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import connect from 'share/enhancers/Izenda.Connect';
import Destination from './Izenda.Destination';
import DestinationGrid from './Izenda.DestinationGrid';
import ImportActionBar from './Izenda.ImportActionBar';
import AdvancedImport from './AdvancedImport/Izenda.AdvancedImport';
import DataModelOverwritePopup from './DataModelOverwritePopup/Izenda.DataModelOverwritePopup';
import SourceTree from './DataModelOverwritePopup/Izenda.SourceTree';


class ImportDataModel extends Component {
  propTypes: {
    store: PropTypes.object.isRequired
  };
 
  render() {
    // Workspace detail store
    const { localization, store, store : { dataModelImport } } = this.props;

    if(dataModelImport.selectedTenant)
      return(<AdvancedImport onValidate={this.onValidate} onClose={this.onClose} store={store} />);

    return (
      <section className='izenda-Wrapper'>
        <ImportActionBar  onCancel={this.onCancel} onImport={this.onImport} alertMessage={dataModelImport.alertMessage} />
        <div className='izenda-Wrapper-content'>
          <div className='panel-heading'>
            <span className='text-bold'>{localization.getText('IMPORT_DATAMODEL_LABEL_SELECTED_FILE')} </span>
            <span>{dataModelImport.name}</span>
          </div>
          <ScrollBox>
            <div className='u_paddingLeftRight'>
              <Destination store={store} />
              <div className='izenda-sourceDestination u_marginTop'>
                <DestinationGrid store={store} />
              </div>
            </div>
          </ScrollBox>
        </div>
      </section>
    );
  }

  onImport = () => {
    const { store : { dataModelImport }, loader, openPopup, localization } = this.props;

    loader.startLoading(); 
    return this.validate()
    .then(success => {
      if(!success){
        return null;
      }
      return this.checkDataSourcesConflictsWhenImportDataModel().then(result => {
        if(!result.success){
          dataModelImport.showAlertMessage('error', localization.getText('COMMON_MESSAGES_GENERIC_ERROR'));
          return null;
        }else if(!isNil(result.tree)) {
          const importDataModel = () => {
            loader.startLoading();
            this.importDataModel().finally(loader.endLoading.bind(loader));
          }

          openPopup(DataModelOverwritePopup, {
            title: localization.getText('IMPORT_DATAMODEL_LABEL_OVERWRITE_NOTIFICATION'),
            treeComponent: SourceTree,
            tree: result.tree,
            importDataModel: importDataModel
          });
          return null;
        }else{
          return this.importDataModel();
        }
      });
    })
    .finally(loader.endLoading.bind(loader));
  }

  onValidate = () => {
    const { loader } = this.props;

    loader.startLoading();
    return this.validate().finally(loader.endLoading.bind(loader));
  }

  checkDataSourcesConflictsWhenImportDataModel = () => {
    const { store, store : { dataModelImport }} = this.props;

    const data = dataModelImport.buildData();
    return store.checkDataSourcesConflictsWhenImportDataModel(data);
  }

  importDataModel = () => {
    const { store, store : { dataModelImport }} = this.props;

    const data = dataModelImport.buildData();
    return store.importDataModel(data).then(result => {
      if (result.success) {
        store.removeFilesSync();
        store.reset();
      }else{
        dataModelImport.showAlertMessage('error', localization.getText('COMMON_MESSAGES_GENERIC_ERROR'));
      }
      return null;
    });
  }

  validate = () => {
    const { store, store : { dataModelImport }, validation, localization } = this.props;

    validation.reset();

    return validation.validate().then((uiValidation) => {
      if(!uiValidation){
        return null;
      }
      const data = dataModelImport.buildData();
      return store.validateImportDataModel(data).then(result => {
        const validatedTenants = result.tenants || [];
        if(!result.success && isEmpty(validatedTenants)){
          dataModelImport.showAlertMessage('error', localization.getText('COMMON_MESSAGES_GENERIC_ERROR'));
        }else{
          forEach(validatedTenant => {
            const tenant = find(t => t.tenantId === validatedTenant.tenantId, dataModelImport.importDataModelTenants);
            tenant.poppulateDataAfterValidate(validatedTenant);

            if(!isEmpty(validatedTenant.messages))
              validation.updateErrors(validatedTenant.messages);

          }, validatedTenants);
        }
        return result.success;
      });
    });
  }

  onCancel = () => {
    const { store } = this.props;
    store.removeFilesSync();
    store.reset();
  }

  onClose = () => {
    const { store : { dataModelImport } } = this.props;
    dataModelImport.resetSelectedTenant();
  }
}

export default connect()(ImportDataModel);



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/Import/DataModel/Izenda.ImportDataModel.jsx